import {
    DotsHorizontalIcon,
} from '@radix-ui/react-icons';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import '../../App.css';
import { AuthContext } from '../../context/AuthContext';
import MarkBunnyDeceasedDialog from '../dialogs/markBunnyDeceasedDialog';
import RetractNewBunnyDialog from '../dialogs/retractNewBunnyDialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
    Dialog,
} from "../ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';

export const columns = (fetchBunnies, auth) => [
    {
        accessorKey: "name",
        header: ({ column }) => <DatatableSortButton column={column}>Namn</DatatableSortButton>,
        cell: info => {
            const bunny = info.row.original;
            return <Link to={`/bunny/${bunny.id}`} className='text-blue-600 font-medium hover:underline'>{bunny.name}</Link>
        }
    },
    {
        accessorKey: "birth_date",
        header: ({ column }) => <DatatableSortButton column={column}>Födelsedatum</DatatableSortButton>,
        cell: info => {
            const bunny = info.row.original;
            const date = new Date(bunny.birth_date);
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });

        }
    },
    {
        accessorKey: "gender",
        header: ({ column }) => <DatatableSortButton column={column}>Kön</DatatableSortButton>,
        cell: info => {
            const bunny = info.row.original;
            // Function to convert gender to Swedish
            const genderInSwedish = (gender) => {
                if (gender === "male") return "Hane";
                if (gender === "female") return "Hona";
                return gender; // fallback in case there are other values
            };

            return genderInSwedish(bunny.gender);
        }
    },
    {
        accessorKey: "status",
        header: ({ column }) => <DatatableSortButton column={column}>Status</DatatableSortButton>,
        cell: info => {
            const bunny = info.row.original;
            if (bunny.death_date !== null) {
                return <Badge variant='destructive'>Avliden</Badge>;
            }
            else if (bunny.approved_at === null) {
                return <Badge variant='outline'>Ansökning</Badge>;
            }
            else if (bunny.status === 'loaned') {
                return <Badge variant='secondary'>Lånad</Badge>;
            } else {
                return <Badge variant=''>Egen</Badge>;
            }
        }
    },
    {
        id: "actions",
        enableHiding: false,
        cell: ({ row }) => {
            const bunny = row.original;
            return (
                <Dialog modal={true}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 float-right">
                                <span className="sr-only">Öppna meny</span>
                                <DotsHorizontalIcon className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => {
                            e.preventDefault();
                        }}>
                            <DropdownMenuLabel>Alternativ</DropdownMenuLabel>
                            {bunny.death_date !== null || bunny.approved_at === null && (
                                <>
                                    {/* <DropdownMenuSeparator /> */}
                                    <RetractNewBunnyDialog bunny={bunny} fetchBunnies={fetchBunnies} />
                                </>
                            )}
                            {bunny.death_date === null && bunny.approved_at !== null && bunny.owner === auth.user?.id && bunny.status !== 'loaned' && (
                                <>
                                    {/* <DropdownMenuSeparator /> */}
                                    <MarkBunnyDeceasedDialog bunny={bunny} fetchBunnies={fetchBunnies} />
                                </>
                            )}

                        </DropdownMenuContent>
                    </DropdownMenu>
                </Dialog>
            );
        }
    },
];

export function MyBunniesDatatable({ bunnies, fetchBunnies, isLoading }) {
    const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [filterInput, setFilterInput] = useState("");
    const { auth } = useContext(AuthContext);

    const [showDeceased, setShowDeceased] = useState(false);

    const handleDeceasedStatus = () => {
        setShowDeceased(prev => !prev);
    };

    const filteredBunnies = useMemo(() => {
        const result = bunnies.filter(bunny => {
            const isDeceased = (bunny.death_date != null);

            // Always include alive bunnies
            if (!isDeceased) {
                return true;
            }
            // Include deceased bunnies if the checkbox is checked
            if (isDeceased && showDeceased) {
                return true;
            }
            // Otherwise, exclude the bunny
            return false;
        });

        return result;
    }, [bunnies, showDeceased]); // Dependencies for memoization

    // Delay function before filtering to reduce bounce
    useEffect(() => {
        const handler = setTimeout(() => {
            table.getColumn("name")?.setFilterValue(filterInput);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [filterInput]);

    const table = useReactTable({
        data: filteredBunnies.length > 0 ? filteredBunnies : [], // Ensure data is always an array
        columns: columns(fetchBunnies, auth),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    return (
        <>
            <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 py-3'>
                <div className='grid grid-cols-2 gap-3'>
                    <div className='flex flex-col gap-2'>
                        <Label htmlFor='searchBunny'>Kanin</Label>
                        <Input
                            id='searchBunny'
                            placeholder="Sök kanin..."
                            value={filterInput}
                            disabled={isLoading || filteredBunnies.length === 0}
                            onChange={event => setFilterInput(event.target.value)}
                            className="max-w-sm"
                        />
                    </div>
                    <div className='flex items-center gap-2'>
                        <Label className='flex gap-2 items-center'>
                            <Checkbox
                                checked={showDeceased}
                                onCheckedChange={handleDeceasedStatus}
                                disabled={isLoading || filteredBunnies.length === 0} />
                            Inkludera avlidna kaniner
                        </Label>
                    </div>
                </div>
            </div>
            <DataTable
                table={table}
                isLoading={isLoading}
                columns={columns(fetchBunnies, auth)}
                skeletonRows={10}
            />
        </>
    );
}
